import React, { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cn from 'classnames';
import Contact from '@models/Contact';
import Section from '@components/Section';
import Container from '@components/ContentContainer';
import Header from './ActivityHeader';
import { List as VirtualizedList, createListContext } from '@components/List/VirtualizedList';
import styles from './Activity.module.scss';
import { useNavigate } from 'react-router-dom';
import { ViewID } from '@reducers/ui/viewRestoration';
import { useAppContactSelecting } from '@hooks/use-app-contact-selecting';
import { useSaveViewState } from '@hooks/use-save-view-state';
import { useScrollRestoration } from '@hooks/use-scroll-restoration';
import rowStyles from './ActivityListItem/ActivityListItem.module.scss';
import {
  dismissActivityItem,
  fetchActivity,
  fetchActivityFilters,
  fetchMoreActivity,
  markActivityAsSeen,
} from '@actions/activity';
import {
  selectActiveActivityFilter,
  selectActivity,
  selectActivityHasNewItems,
} from '@reducers/data/activity';
import { selectSavedViewState } from '@actions/ui';
import { TimeSeparator } from '@components/List/VirtualizedList/utils/time-separators';
import ActivityListItem from './ActivityListItem';
import { fetchNexusStatus } from '@actions/nexus';
import { ActivitySidebar } from './ActivitySidebar';
import { ActivityItem } from '@models/Activity';
import { metaTrigger } from '@support/keyboardHandler';
import { ID } from '@app/types';
import ClearedActivity from './ClearedActivity';
import { useOnVisible } from '@support/hooks/use-on-visible';
import useEnabledNotifications from '@views/Settings/panels/Notifications/use-enabled-notifications';

export interface ActivityViewState {
  activeActivityItem?: ActivityItem;
  activeNested: boolean;
  nestedItem?: Contact;
}

type ActivityViewAction =
  | ['focusMainList']
  | ['focusNestedList', Contact]
  | ['selectActiveItem', ActivityItem]
  | ['clearSelections'];

const activityViewReducer = (
  state: ActivityViewState,
  [type, value]: ActivityViewAction,
): ActivityViewState => {
  switch (type) {
    case 'selectActiveItem':
      return { ...state, activeActivityItem: value, activeNested: false, nestedItem: null };

    case 'focusMainList':
      return { ...state, activeNested: false, nestedItem: null };

    case 'focusNestedList':
      return { ...state, activeNested: true, nestedItem: value };

    case 'clearSelections':
      return { ...state, activeActivityItem: null, activeNested: false, nestedItem: null };

    default:
      return state;
  }
};

const viewID: ViewID = 'Activity';

const ActivityListContext = createListContext();

export default function Activity() {
  const dispatchToStore = useDispatch();
  const navigate = useNavigate();
  const { enabledNotifications } = useEnabledNotifications();
  const scrollingContainerRef = useRef(null);
  const listRef = useRef(null);
  const activity = useSelector(selectActivity);
  const activityFilter = useSelector(selectActiveActivityFilter);
  const activityHasNewItems = useSelector(selectActivityHasNewItems);
  const savedViewState = useSelector(selectSavedViewState(viewID));
  const [resetOnNextUpdate, setResetOnNextUpdate] = useState(false);
  const [viewInFocus, setViewInFocus] = useState(true);
  const [viewVisible, setViewVisible] = useState(true);
  const viewScrolledAtMount = useScrollRestoration({ viewID: 'Activity', scrollingContainerRef });

  const [{ activeActivityItem, activeNested, nestedItem }, dispatchToInbox] = useReducer(
    activityViewReducer,
    {
      activeActivityItem: savedViewState?.activeActivityItem || null,
      activeNested: false, //savedViewState?.activeNested || false,
      nestedItem: null, //savedViewState?.nestedItem || null,
    },
  );

  const activityItems = useMemo(() => activity.sortedItems, [activity.items]);
  const multiplePeoplePerReconnect = enabledNotifications['activity_group_reconnects'];

  // TODO: this is hacky as shit
  // the correct size should be getting determined by item
  useEffect(() => {
    if (resetOnNextUpdate) {
      setResetOnNextUpdate(false);
      listRef.current?.resetAfterIndex(0);
    }
  }, [activity.updated]);

  useSaveViewState(viewID, { activeActivityItem, activeNested, nestedItem }, [
    activeActivityItem,
    activeNested,
    nestedItem,
  ]);
  useAppContactSelecting({
    contact:
      (activeActivityItem?.type !== 'shared' && nestedItem) ||
      activeActivityItem?.contact ||
      (activeActivityItem?.filteredContacts?.length === 1 &&
        activeActivityItem.filteredContacts[0]),
    source: `activity${nestedItem ? '-nested' : '-'}list-item`,
    additionalDeps: [activeNested],
  });

  useEffect(() => {
    !viewScrolledAtMount && dispatchToStore(fetchActivity());
    dispatchToStore(fetchNexusStatus());
    dispatchToStore(fetchActivityFilters());
    dispatchToStore(markActivityAsSeen());
  }, []);

  useEffect(() => {
    if (activityHasNewItems && viewInFocus && viewVisible) {
      dispatchToStore(markActivityAsSeen());
    }
  }, [activityHasNewItems, viewInFocus, viewVisible]);

  useOnVisible(() => {
    if (!activeActivityItem || activity.items.keySeq().indexOf(activeActivityItem.id) < 10) {
      dispatchToStore(fetchActivity());
    }
  }, [activeActivityItem]);

  useEffect(() => {
    const focusHandler = () => setViewInFocus(true);
    const blurHandler = () => setViewInFocus(false);

    const visibilityHandler = () => {
      if (!document.hidden) {
        setViewVisible(true);
      } else {
        setViewVisible(false);
      }
    };

    window.addEventListener('focus', focusHandler);
    window.addEventListener('blur', blurHandler);
    window.addEventListener('visibilitychange', visibilityHandler);
    return () => {
      window.removeEventListener('focus', focusHandler);
      window.removeEventListener('blur', blurHandler);
      window.removeEventListener('visibilitychange', visibilityHandler);
    };
  }, []);

  const defaultSelectedIdx = useMemo(() => {
    let idx = 0;
    if (savedViewState?.activeActivityItem) {
      idx = activity.items
        .keySeq()
        .findIndex((key) => key == savedViewState.activeActivityItem?.id);
    }
    return idx;
  }, []);

  const itemBasedListRowClassName = (id: ID) => {
    const item = activity.items.get(id);
    let className = undefined;

    if (item?.contact) {
      className = cn(className, rowStyles.selectableListItem);
    }

    if (!item.seen) {
      className = cn(className, rowStyles.unseenListItem);
    }

    return className;
  };

  const handleContactSelect = (contact: Contact) => {
    contact && navigate(contact.link);
  };

  const handleItemSelect = (activityItem: ActivityItem) => {
    if (activityItem?.contact) {
      return handleContactSelect(activityItem.contact);
    }
  };

  const handleMainListItemHotKey = (item: ActivityItem, e: KeyboardEvent) => {
    // right arrow nav into nested list
    if (
      (item &&
        (item.metadata?.contacts?.length > 1 || item.metadata?.sharedGroup) &&
        e.keyCode === 39) ||
      e.keyCode === 9 ||
      e.key == 'Tab'
    ) {
      e.preventDefault();
      return dispatchToInbox(['focusNestedList', undefined]);
    }

    if (
      item &&
      !['paddingRow', 'sectionHeader'].includes(item.id) &&
      e.key === 'e' &&
      !metaTrigger(e)
    ) {
      dispatchToStore(dismissActivityItem({ id: item.id }));
    }
  };

  const handleNestedListItemHotKey = (contact: Contact, e: KeyboardEvent) => {
    // left arrow nav back to main list
    if (contact && e.keyCode == 37 && activeNested) {
      e.preventDefault();
      return dispatchToInbox(['focusMainList']);
    }
  };

  const determineItemSize = (item: ActivityItem) => {
    if (item?.metadata?.subtype === 'connect-integrations') {
      return 137;
    }

    if (item?.metadata?.subtype === 'search-suggestions') {
      return 112;
    }

    if (item?.type === 'reconnect' && !multiplePeoplePerReconnect) {
      return 58;
    }

    if (
      (item?.type === 'import' && item?.metadata?.importTotal > 1) ||
      ['reconnect', 'announcement'].includes(item?.type)
    ) {
      return 86;
    }

    if (item?.type === 'news') {
      return 132;
    }

    if (
      item?.type === 'post' &&
      (item?.metadata?.source !== 'linkedin' || item?.metadata?.postSubtype !== 'announcement')
    ) {
      return 108;
    }

    if (item?.metadata?.subtype === 'bio') {
      return 108;
    }

    if (item?.type === 'diff') {
      return 86;
    }

    return 58;
  };

  const renderActivityItems = () => {
    if (activity.isLoaded && activity.items.size === 0) {
      return <ClearedActivity />;
    }

    return (
      <VirtualizedList
        key={String(activityFilter)}
        active={!activeNested}
        items={activityItems}
        ListContext={ActivityListContext}
        listRef={listRef}
        ref={scrollingContainerRef}
        shouldResetHighlight={false}
        canLoadMore={activity.canLoadMore}
        loadMore={() => dispatchToStore(fetchMoreActivity())}
        onItemHighlight={(item: ActivityItem) => dispatchToInbox(['selectActiveItem', item])}
        // @ts-ignore
        onHotKey={handleMainListItemHotKey}
        listRowClassName={rowStyles.mainListItem}
        itemBasedListRowClassName={itemBasedListRowClassName}
        determineItemSize={determineItemSize}
        onItemClick={handleItemSelect}
        renderItem={(item: ActivityItem, isHighlighted) => {
          if ((item as unknown as TimeSeparator).type === 'time-separator') {
            return (
              <div className={styles.timeSeparator}>{(item as unknown as TimeSeparator).label}</div>
            );
          }

          return <ActivityListItem key={item.id} item={item} selected={isHighlighted} />;
        }}
        timeBucketKey="date"
        defaultHighlightedIdx={defaultSelectedIdx}
        disabled={activity.isDeleting}
      />
    );
  };

  return (
    <Container key={String(multiplePeoplePerReconnect)}>
      <Container.Primary className={styles.primary} id="content" header={<Header />}>
        <Section>{renderActivityItems()}</Section>
      </Container.Primary>
      <ActivitySidebar
        activityItemID={activeActivityItem?.id}
        setActiveNested={(contact: Contact) => dispatchToInbox(['focusNestedList', contact])}
        active={activeNested}
        onHotKey={handleNestedListItemHotKey}
        onSelect={handleContactSelect}
        focusMainList={() => dispatchToInbox(['focusMainList'])}
      />
    </Container>
  );
}
